import { ActionIcon, Badge, Breadcrumbs, Button, Menu, rem, Tabs, Text, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import type { RunStatus } from '@prisma/client';
import {
  IconChevronDown,
  IconDotsVertical,
  IconExternalLink,
  IconMessage,
  IconPlayerPlayFilled,
  IconRefresh,
  IconReportAnalytics,
  IconShieldCog,
  IconTrash,
} from '@tabler/icons-react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { prettyDuration } from '~/common/time/duration';
import RunHistoryHomeCrumb from '~/components/breadcrumbs/crumbs/history/RunHistoryHomeCrumb';
import { GitBranchBadge } from '~/components/common/GitBranchBadge';
import { HeadTitle } from '~/components/common/HeadTitle';
import RunDetailsView from '~/components/history/RunDetailsView';
import { DataTable } from '~/components/tables/common/DataTable';
import DataTableSearchBox from '~/components/tables/common/DataTableSearchBox';
import { useDataTable } from '~/components/tables/common/useDataTable';
import { getEarliestStartTime, testRunColumns } from '~/components/tables/testRun/testRunColumns';
import { TestDrawer } from '~/components/test/TestDrawer';
import { RunNoteHoverCard } from '~/components/testRun/note/RunNoteHoverCard';
import ReRunModal from '~/components/testRun/ReRunModal';
import { TestRunDrawer } from '~/components/testRun/TestRunDrawer';
import CanNotStartTesting from '~/components/testSuite/CanNotStartTesting';
import { TestSuiteHeatMap } from '~/components/testSuite/heatmap/TestSuiteHeatMap';
import ManualTriggerAdvanced from '~/components/testSuite/ManualTriggerAdvanced';
import { useProjectId } from '~/hooks/context/useProjectId';
import { useQueryParam } from '~/hooks/nav/useQueryParam';
import { useIntervalEffect } from '@react-hookz/web';
import { useTestReRun } from '~/hooks/test/useTestReRun';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { downloadFile } from '~/logic/file/download';
import { queryParamToString } from '~/logic/query-param';
import { api } from '~/trpc-extra/api';

type StatusCount = {
  status: RunStatus;
  count: number;
  color: string;
};

const COST_PER_MINUTE_USD = 0.01;

export default function RunPage() {
  const trpcUtils = api.useUtils();

  const projectId = useProjectId();
  const router = useRouter();
  const { isAdmin } = useAuthData();
  const runId = queryParamToString(router.query.runOrSuiteId).split('_')[1];
  const [tab, setTab] = useQueryParam('tab', 'runs');

  const { data: suiteRun, isLoading: isSuiteRunLoading } = api.suiteRun.get.useQuery(
    { runId: runId! },
    { enabled: !!runId },
  );

  const { data: canRunTests, isLoading: canRunTestsLoading } = api.project.getCanRunTests.useQuery({
    projectId: projectId!,
  });

  // Load hasLinearInstalled once for all rows
  const { data: hasLinearInstalled } = api.project.hasLinearInstalled.useQuery(
    { projectId: projectId || '' },
    { enabled: !!projectId },
  );

  const showWarningOnRerun = suiteRun?.status === 'RUNNING';
  const { handleReRun, isReRunning } = useTestReRun({ showWarning: showWarningOnRerun });

  const { mutateAsync: exportReport } = api.suiteRun.exportReport.useMutation();
  const { mutateAsync: clearSuiteRunHistory } = api.testSuite.clearRunHistory.useMutation();
  const cancelSuite = api.testSuite.cancel.useMutation();
  const [isCanceling, setIsCanceling] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const testRuns = useMemo(() => suiteRun?.testRuns || [], [suiteRun?.testRuns]);

  // Load issues for all test runs in parallel
  const issuesQueries = api.useQueries((t) =>
    testRuns.map((run) => t.testRun.getIssues({ id: { runId: run.id } }, { enabled: !!run.id })),
  );

  // Map issues by run ID for easy lookup
  const issuesByRunId = useMemo(() => {
    const map = new Map<string, (typeof issuesQueries)[number]['data']>();
    testRuns.forEach((run, index) => {
      map.set(run.id, issuesQueries[index]?.data ?? []);
    });
    return map;
  }, [testRuns, issuesQueries]);

  const passedCount = testRuns.filter(({ status }) => status === 'PASSED').length;
  const skippedCount = testRuns.filter(({ status }) => status === 'SKIPPED').length;
  const failedCount = testRuns.filter(({ status }) => status === 'FAILED').length;
  const flakyCount = testRuns.filter(({ status }) => status === 'FLAKY').length;
  const runningCount = testRuns.filter(({ status }) => status === 'RUNNING').length;
  const retryingCount = testRuns.filter(({ status }) => status === 'RETRYING').length;
  const queuedCount = testRuns.filter(({ status }) => status === 'QUEUED').length;

  const [activeFilters, setActiveFilters] = useState<RunStatus[]>([]);

  const isRefreshEnabled = useMemo(() => {
    return suiteRun?.status === 'RUNNING';
  }, [suiteRun]);

  const totalDurationMs = useMemo(() => {
    return suiteRun?.testRuns.reduce((acc, run) => acc + (run.durationMs ?? 0), 0) ?? 0;
  }, [suiteRun]);

  // Calculate total duration from all test run attempts (for admin tab)
  const totalAttemptsDurationMs = useMemo(() => {
    if (!suiteRun?.testRuns) {
      return 0;
    }
    return suiteRun.testRuns.reduce((total, run) => {
      const attemptsDuration = run.TestRunAttempts.reduce((acc, attempt) => acc + (attempt.durationMs ?? 0), 0);
      return total + attemptsDuration;
    }, 0);
  }, [suiteRun]);

  const isTempSuite = suiteRun?.testSuite.temp ?? false;
  const primaryTestName =
    suiteRun?.testRuns.find((run) => run.test?.name)?.test?.name ??
    suiteRun?.testSuite.suiteTests.find((suiteTest) => !suiteTest.skip)?.testV2?.name;
  const runDisplayName =
    isTempSuite ?
      (primaryTestName ?? suiteRun?.testSuite.name ?? 'Single Test Run')
    : (suiteRun?.testSuite.name ?? 'Test Suite Run');

  // Fetch trend data once for all tests (avoid N+1 query pattern)
  const { data: trendData } = api.suiteRun.getRecentHistory.useQuery(
    { testSuiteId: suiteRun?.testSuiteId || '', take: 10 },
    { enabled: !!suiteRun?.testSuiteId && !isTempSuite },
  );

  useEffect(() => {
    if (isTempSuite && tab === 'heatmap') {
      setTab('runs');
    }
  }, [isTempSuite, tab, setTab]);

  useIntervalEffect(
    () => {
      trpcUtils.suiteRun.get.invalidate({ runId: runId! }).catch(() => undefined);
    },
    isRefreshEnabled ? 10000 : undefined,
  );

  function rerunTests(runIds: string[]) {
    modals.open({
      modalId: 'rerun-config-modal',
      title: 'Rerun Settings',
      size: 'md',
      children: (
        <ReRunModal
          onSubmit={async () => {
            await handleReRun(runIds);
          }}
        />
      ),
    });
  }

  const crumbs = useMemo(
    () => [
      <RunHistoryHomeCrumb
        projectId={projectId || ''}
        showIcon={true}
        key="runHistory"
      />,
    ],
    [projectId],
  );

  // used to display run options, okay since we can assume run options are the same for all test runs
  const firstRegTestRun = useMemo(
    () => testRuns.find((x) => x.status !== 'SKIPPED' && x.runType === 'TEST'),
    [testRuns],
  );

  const sortedTestRuns = useMemo(() => {
    const filteredRuns = testRuns.filter(({ status }) => activeFilters.length === 0 || activeFilters.includes(status));

    return filteredRuns.sort((a, b) => {
      const startTimeA = getEarliestStartTime(a.TestRunAttempts);
      const startTimeB = getEarliestStartTime(b.TestRunAttempts);

      if (!startTimeB) {
        return -1;
      }
      if (!startTimeA) {
        return 1;
      }
      return startTimeA.getTime() - startTimeB.getTime();
    });
  }, [testRuns, activeFilters]);

  // Prepare data for the table
  const tableData = useMemo(() => {
    return sortedTestRuns.map((run) => ({
      ...run,
      suiteId: suiteRun?.testSuiteId || '',
      showWarning: showWarningOnRerun,
      suiteRunStatus: suiteRun?.status,
      isLatestSuiteRun: suiteRun?.isLatestSuiteRun,
      suiteRunId: suiteRun?.id,
      issues: issuesByRunId.get(run.id) ?? [],
      hasLinearInstalled: hasLinearInstalled ?? false,
      trendData,
    }));
  }, [sortedTestRuns, showWarningOnRerun, suiteRun, issuesByRunId, hasLinearInstalled, trendData]);

  const table = useDataTable({
    columns: testRunColumns({
      // Temporarily hiding autoheal suggestions, only show column for skip suggestions
      showSuggestionColumn: tableData.some((x) => x.skipSuggestion),
      // Show trend column for non-temp suites (temp suites are single-run with no history)
      showTrendColumn: !isTempSuite,
    }),
    data: tableData,
    enableQueryParam: true,
    storageKey: 'testRunTable',
    pinnedColumns: {
      left: ['status', 'name'],
    },
  });

  const statusCounts: StatusCount[] = [
    {
      status: 'PASSED',
      count: passedCount,
      color: 'green',
    },
    {
      status: 'FLAKY',
      count: flakyCount,
      color: '#90a959',
    },
    {
      status: 'FAILED',
      count: failedCount,
      color: 'red',
    },
    {
      status: 'RUNNING',
      count: runningCount,
      color: '#c9a11e',
    },
    {
      status: 'RETRYING',
      count: retryingCount,
      color: '#c97f1e',
    },
    {
      status: 'QUEUED',
      count: queuedCount,
      color: 'dimgray',
    },
    {
      status: 'SKIPPED',
      count: skippedCount,
      color: 'gray',
    },
  ];

  return (
    <>
      <HeadTitle
        title={runDisplayName}
        suffix="Run History"
      />
      <div className="flex h-full flex-col overflow-hidden">
        <div className="flex-shrink-0 px-8">
          <Breadcrumbs
            separator=">"
            mt="sm"
            className="mb-1 text-xs"
          >
            {crumbs}
          </Breadcrumbs>
          <div className="py-4">
            <div className="flex flex-row justify-between gap-4">
              <div className="flex flex-col">
                <div className="flex items-start space-x-2">
                  {isSuiteRunLoading ?
                    <div className="h-7 w-48 animate-pulse rounded bg-stone-200 dark:bg-stone-700" />
                  : isTempSuite ?
                    <div className="text-xl font-bold text-stone-800 dark:text-stone-100">{runDisplayName}</div>
                  : <Link href={`/project/${projectId}/testSuite/${suiteRun?.testSuite.id || ''}`}>
                      <div className="text-xl font-bold hover:text-stone-800 hover:underline dark:hover:text-stone-200">
                        {runDisplayName}
                      </div>
                    </Link>
                  }
                  <div className="text-xl font-medium">#{(runId || '')?.slice(0, 8)}</div>
                </div>
              </div>
              <div className="flex flex-col">
                {isSuiteRunLoading ?
                  <div className="h-10 w-24 animate-pulse rounded bg-stone-200 dark:bg-stone-700" />
                : suiteRun ?
                  <div className="flex flex-row items-center gap-2">
                    {!isTempSuite && (
                      <Button
                        disabled={suiteRun.status === 'RUNNING' || canRunTestsLoading}
                        leftSection={<IconPlayerPlayFilled style={{ width: rem(14), height: rem(14) }} />}
                        onClick={() => {
                          if (!canRunTests) {
                            modals.open({
                              size: 'sm',
                              centered: true,
                              children: <CanNotStartTesting />,
                            });
                          } else {
                            modals.open({
                              title: 'Manual Trigger',
                              size: '60%',
                              children: <ManualTriggerAdvanced testSuiteId={suiteRun.testSuite.id} />,
                            });
                          }
                        }}
                      >
                        Start A New Run
                      </Button>
                    )}
                    <Button
                      leftSection={<IconReportAnalytics size={16} />}
                      variant="outline"
                      className="border-stone-500 text-stone-700 dark:border-stone-500 dark:text-stone-300"
                      onClick={async () => {
                        if (isDownloading) {
                          return;
                        }
                        setIsDownloading(true);

                        try {
                          const result = await exportReport({ runId: suiteRun.id });
                          downloadFile(result.content, { filename: result.filename });
                          notifications.show({ message: 'Report downloaded successfully' });
                        } catch (error) {
                          notifications.show({
                            message: 'Failed to download report',
                            color: 'red',
                          });
                        } finally {
                          setTimeout(() => {
                            setIsDownloading(false);
                          }, 500);
                        }
                      }}
                      disabled={isDownloading}
                    >
                      Download Report
                    </Button>

                    {suiteRun.status === 'RUNNING' && (
                      <Button
                        variant="outline"
                        loading={isCanceling}
                        onClick={async () => {
                          modals.openConfirmModal({
                            title: 'Please confirm your action',
                            children: <Text size="sm">Cancel this run?</Text>,
                            labels: { confirm: 'Yes', cancel: 'No' },
                            confirmProps: { color: 'red' },
                            onConfirm: async () => {
                              notifications.show({ message: 'Cancelling run...' });
                              try {
                                setIsCanceling(true);
                                await cancelSuite.mutateAsync({ testSuiteRunId: suiteRun.id });
                                notifications.show({ message: 'Run cancelled' });
                              } catch (e) {
                                notifications.show({ message: 'Failed to cancel run', color: 'red' });
                                console.error(e);
                              } finally {
                                setIsCanceling(false);
                              }
                              await trpcUtils.suiteRun.get.invalidate({ runId: suiteRun.id }).catch(() => undefined);
                            },
                          });
                        }}
                      >
                        Cancel
                      </Button>
                    )}

                    <Menu
                      transitionProps={{ transition: 'pop-top-right' }}
                      position="top-end"
                      width={220}
                      withinPortal
                      shadow="lg"
                    >
                      <Menu.Target>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="lg"
                        >
                          <IconDotsVertical size={18} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {!isTempSuite && (
                          <Menu.Item
                            leftSection={
                              <IconExternalLink
                                size={16}
                                stroke={1.5}
                              />
                            }
                            component={Link}
                            href={`/project/${projectId}/testSuite/${suiteRun?.testSuite.id || ''}`}
                          >
                            Suite Settings
                          </Menu.Item>
                        )}
                        <Menu.Item
                          color="red"
                          leftSection={<IconTrash size={16} />}
                          onClick={async () => {
                            if (!projectId) {
                              notifications.show({
                                message: 'Invalid project ID',
                                color: 'red',
                              });
                              return;
                            }
                            modals.openConfirmModal({
                              title: 'Delete Test Run',
                              children: (
                                <Text size="sm">
                                  Are you sure you want to delete this test run? This action cannot be undone.
                                </Text>
                              ),
                              labels: { confirm: 'Delete', cancel: 'Cancel' },
                              confirmProps: { color: 'red' },
                              onConfirm: async () => {
                                try {
                                  await clearSuiteRunHistory({
                                    projectId,
                                    testSuiteId: suiteRun.testSuite.id,
                                    testSuiteRunId: suiteRun.id,
                                  });
                                  notifications.show({ message: 'Test run deleted successfully' });
                                  await router.push(`/project/${projectId}/history`);
                                } catch (error) {
                                  notifications.show({
                                    message: 'Failed to delete test run',
                                    color: 'red',
                                  });
                                  console.error(error);
                                }
                              },
                            });
                          }}
                        >
                          Delete Test Run
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>

                    <Menu
                      transitionProps={{ transition: 'pop-top-right' }}
                      position="top-end"
                      width={220}
                      withinPortal
                      shadow="lg"
                    >
                      <Menu.Target>
                        <Button
                          leftSection={
                            <IconRefresh
                              size={18}
                              stroke={1.5}
                            />
                          }
                          rightSection={
                            <IconChevronDown
                              size={16}
                              stroke={1.5}
                            />
                          }
                          pr={12}
                          disabled={isReRunning}
                          className="hidden"
                        >
                          Rerun
                        </Button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={
                            <IconRefresh
                              size={16}
                              stroke={1.5}
                            />
                          }
                          onClick={() =>
                            rerunTests(sortedTestRuns.filter((x) => x.status !== 'SKIPPED').map((x) => x.id))
                          }
                        >
                          Rerun all tests
                        </Menu.Item>
                        <Menu.Item
                          leftSection={
                            <IconRefresh
                              size={16}
                              stroke={1.5}
                              color="red"
                            />
                          }
                          disabled={!sortedTestRuns.some((x) => x.status === 'FAILED')}
                          onClick={() =>
                            rerunTests(sortedTestRuns.filter((x) => x.status === 'FAILED').map((x) => x.id))
                          }
                        >
                          Rerun failed tests
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </div>
                : null}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex flex-row items-center justify-between">
                <div className="flex items-center gap-4">
                  {isSuiteRunLoading ?
                    <div className="h-6 w-32 animate-pulse rounded bg-stone-200 dark:bg-stone-700" />
                  : <div className="flex flex-row items-center gap-4 text-sm text-stone-600 dark:text-stone-400">
                      <span>
                        Started {suiteRun?.createdAt.toLocaleString()} - Duration:{' '}
                        {suiteRun?.status === 'RUNNING' ?
                          <span>N/A</span>
                        : prettyDuration(Math.ceil((suiteRun?.durationMs ?? totalDurationMs) / 1000))}
                      </span>
                      {!!suiteRun?.note && (
                        <RunNoteHoverCard note={suiteRun.note}>
                          <Button
                            variant="default"
                            color="blue"
                            size="compact-md"
                            radius="md"
                            leftSection={<IconMessage size={14} />}
                            className="text-xs"
                          >
                            Note
                          </Button>
                        </RunNoteHoverCard>
                      )}
                      {!!suiteRun?.gitBranch && (
                        <GitBranchBadge
                          gitBranch={suiteRun.gitBranch}
                          size="md"
                        />
                      )}
                    </div>
                  }
                </div>
              </div>

              <div className="flex items-center gap-4">
                {isSuiteRunLoading ?
                  <div className="flex gap-2">
                    <div className="h-6 w-20 animate-pulse rounded bg-stone-200 dark:bg-stone-700" />
                    <div className="h-6 w-20 animate-pulse rounded bg-stone-200 dark:bg-stone-700" />
                    <div className="h-6 w-20 animate-pulse rounded bg-stone-200 dark:bg-stone-700" />
                  </div>
                : <div className="flex flex-row items-center justify-center gap-1">
                    {statusCounts
                      .filter(({ count }) => count > 0)
                      .map(({ status, count, color }) => (
                        <Tooltip
                          key={status}
                          className="lowercase first-letter:uppercase"
                          label={status}
                        >
                          <Badge
                            key={status}
                            onClick={() => {
                              if (activeFilters.includes(status)) {
                                setActiveFilters(activeFilters.filter((f) => f !== status));
                              } else {
                                setActiveFilters([status]);
                              }
                            }}
                            className="cursor-pointer"
                            radius="sm"
                            color={color}
                            variant={
                              activeFilters.length === 0 ? 'filled'
                              : activeFilters.includes(status) ?
                                'filled'
                              : 'light'
                            }
                          >
                            <div className="flex items-center gap-1">{count}</div>
                          </Badge>
                        </Tooltip>
                      ))}
                    {activeFilters.length > 0 && (
                      <Button
                        size="xs"
                        variant="subtle"
                        onClick={() => setActiveFilters([])}
                        className="ml-2 h-5"
                      >
                        Clear Filter
                      </Button>
                    )}
                  </div>
                }
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-1 flex-col overflow-hidden px-8">
          <Tabs
            className="flex flex-1 flex-col overflow-hidden"
            value={tab || 'runs'}
            onChange={setTab}
          >
            <div className="sticky top-0 z-30 flex-shrink-0 border-b border-gray-200 dark:border-gray-700">
              <Tabs.List className="border-b-0">
                <Tabs.Tab value="runs">Result</Tabs.Tab>
                <Tabs.Tab value="runConfig">Configuration</Tabs.Tab>
                {tab === 'runs' && (
                  <div className="absolute right-0 mt-px w-24 sm:w-48">
                    <DataTableSearchBox table={table} />
                  </div>
                )}
                {!isTempSuite && <Tabs.Tab value="heatmap">Heat Map</Tabs.Tab>}
                {isAdmin && (
                  <Tabs.Tab
                    value="admin"
                    className="text-orange-500"
                  >
                    <div className="flex items-center gap-1">
                      <IconShieldCog size={14} />
                      Admin
                    </div>
                  </Tabs.Tab>
                )}
              </Tabs.List>
            </div>

            <Tabs.Panel
              value="runs"
              className="flex flex-1 flex-col overflow-hidden"
            >
              {isSuiteRunLoading ?
                <div className="px-2 py-8 font-medium">Loading test results...</div>
              : sortedTestRuns.length === 0 ?
                <div className="px-2 py-8 font-medium">Results will be reported here when tests finish running.</div>
              : <DataTable
                  table={table}
                  isLoading={isSuiteRunLoading}
                  emptyMessage="No test results found"
                  classNames={{
                    header: 'sticky top-0 z-40',
                    pinnedCell: '',
                  }}
                />
              }
            </Tabs.Panel>

            <Tabs.Panel
              value="runConfig"
              className="flex-1 overflow-auto pb-8"
            >
              <div className="px-2 py-2">
                <RunDetailsView
                  projectId={projectId || ''}
                  suiteRun={suiteRun}
                  isSuiteRunLoading={isSuiteRunLoading}
                  firstRegTestRun={firstRegTestRun}
                />
              </div>
            </Tabs.Panel>

            {!isTempSuite && (
              <Tabs.Panel
                value="heatmap"
                className="flex flex-1 flex-col overflow-hidden"
              >
                <TestSuiteHeatMap
                  testSuiteId={suiteRun?.testSuite.id || ''}
                  suiteRunId={suiteRun?.id}
                  isActive={tab === 'heatmap'}
                />
              </Tabs.Panel>
            )}

            {isAdmin && (
              <Tabs.Panel
                value="admin"
                className="flex-1 overflow-auto pb-8"
              >
                <div className="px-2 py-4">
                  <div className="rounded-lg border border-stone-200 bg-stone-50 p-6 dark:border-stone-700 dark:bg-stone-800">
                    <h3 className="mb-4 text-lg font-semibold text-stone-800 dark:text-stone-100">
                      Duration Statistics
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between border-b border-stone-200 pb-3 dark:border-stone-700">
                        <div>
                          <div className="font-medium text-stone-700 dark:text-stone-200">
                            Total Duration (All Attempts)
                          </div>
                          <div className="text-sm text-stone-500 dark:text-stone-400">
                            Sum of all test run attempts duration
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-stone-800 dark:text-stone-100">
                            {prettyDuration(Math.ceil(totalAttemptsDurationMs / 1000))}
                          </div>
                          <div className="text-sm text-stone-500 dark:text-stone-400">
                            {(totalAttemptsDurationMs / 60000).toFixed(2)} minutes
                          </div>
                          <div className="text-sm font-medium text-green-600 dark:text-green-400">
                            ${((totalAttemptsDurationMs / 60000) * COST_PER_MINUTE_USD).toFixed(4)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between border-b border-stone-200 pb-3 dark:border-stone-700">
                        <div>
                          <div className="font-medium text-stone-700 dark:text-stone-200">Suite Run Duration</div>
                          <div className="text-sm text-stone-500 dark:text-stone-400">
                            Wall-clock time for the suite run
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-semibold text-stone-700 dark:text-stone-200">
                            {suiteRun?.status === 'RUNNING' ?
                              <span className="text-yellow-600">Running...</span>
                            : prettyDuration(Math.ceil((suiteRun?.durationMs ?? totalDurationMs) / 1000))}
                          </div>
                          <div className="text-sm text-stone-500 dark:text-stone-400">
                            {suiteRun?.status !== 'RUNNING' &&
                              `${((suiteRun?.durationMs ?? totalDurationMs) / 60000).toFixed(2)} minutes`}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-stone-700 dark:text-stone-200">Total Test Runs</div>
                          <div className="text-sm text-stone-500 dark:text-stone-400">
                            Number of tests in this suite run
                          </div>
                        </div>
                        <div className="text-xl font-semibold text-stone-700 dark:text-stone-200">
                          {testRuns.length}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-stone-700 dark:text-stone-200">Total Attempts</div>
                          <div className="text-sm text-stone-500 dark:text-stone-400">
                            Total number of test run attempts (including retries)
                          </div>
                        </div>
                        <div className="text-xl font-semibold text-stone-700 dark:text-stone-200">
                          {testRuns.reduce((acc, run) => acc + run.TestRunAttempts.length, 0)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Tabs.Panel>
            )}
          </Tabs>
        </div>
      </div>
      <TestDrawer />
      <TestRunDrawer />
    </>
  );
}
